import { Box, Divider, Flex, Text, VStack } from '@chakra-ui/react'
import { UseConsentFormsStatus } from '@hb/react'
import { colors, MissingConsentForm } from '@hb/shared'
import { useMemo, useState } from 'react'
import { ElectronicConsentPreview } from '../../Users/Profile/ConsentForms/ElectronicConsent/ElectronicConsentModal'
import { ElectronicConsentProvider } from '../../Users/Profile/ConsentForms/ElectronicConsent/ElectronicConsentProvider'
import { MissingForms } from '../../Users/Profile/ConsentForms/MissingForms'
import { DocumentViewModal } from '../DocumentView/DocumentViewModal'
import { ConsentFormStatusView } from './ConsentFormStatusView'

const ConsentFormSectionHeader = ({ title }: { title: string }) => (
  <Text
    w="100%"
    bg={`${colors.green.hex}10`}
    py={1}
    px={3}
    fontSize="lg"
    color="gray.600"
    fontWeight={500}>
    {title}
  </Text>
)

export const ConsentFormsStatusAdminView = ({
  consentFormsStatus,
  patientId,
  assessmentId,
}: {
  consentFormsStatus: UseConsentFormsStatus
  patientId: string
  assessmentId: string | null
}) => {
  const {
    hbConsentForms,
    practiceConsentForms,
    selectedConsentForm,
    // consentForms,
    handleViewConsentForm,
    handleCloseModal,
    onSignConsentForm,
    missingConsentForms,
    canDownload,
  } = consentFormsStatus ?? {}

  const [electronicConsentModalOpen, setElectronicConsentModalOpen] = useState(false)

  return (
    <ElectronicConsentProvider
      patientId={patientId}
      modalOpen={electronicConsentModalOpen}
      setModalOpen={setElectronicConsentModalOpen}>
      <Flex flexDir="column" gap={3} w="100%">
        <Flex bg="white" _dark={{ bg: 'gray.900' }} flexFlow="column" w="100%">
          <ElectronicConsentPreview openModal={() => setElectronicConsentModalOpen(true)} />
          <ConsentFormSectionHeader title="Hamilton Billing" />
          <VStack spacing={0} alignItems="flex-start" w="100%" divider={<Divider />}>
            {hbConsentForms.length ? (
              hbConsentForms.map(form => (
                // const consentForm = consentForms?.[form.consentFormId]
                //   ? { id: form.consentFormId, ...consentForms[form.consentFormId] }
                //   : null
                <ConsentFormStatusView
                  key={form.consentFormId}
                  patientId={patientId}
                  formStatus={form}
                  // consentForm={consentForm}
                  adminView
                  handleViewConsentForm={handleViewConsentForm}
                />
              ))
            ) : (
              <Text px={4} py={2} bg="white" width="100%" color="gray.600">
                No Hamilton Billing consent forms required
              </Text>
            )}
          </VStack>
          {practiceConsentForms.length ? (
            practiceConsentForms.map(practice => (
              <Flex flexDir="column" w="100%" key={practice.practiceId}>
                <ConsentFormSectionHeader title={practice.practice?.name ?? 'Practice'} />
                <VStack spacing={0} alignItems="flex-start" w="100%" divider={<Divider />}>
                  {practice.forms.map(form => (
                    // const consentForm = consentForms?.[form.consentFormId]
                    //   ? { id: form.consentFormId, ...consentForms[form.consentFormId] }
                    //   : null
                    <ConsentFormStatusView
                      key={form.consentFormId}
                      patientId={patientId}
                      formStatus={form}
                      // consentForm={consentForm}
                      adminView
                      handleViewConsentForm={handleViewConsentForm}
                    />
                  ))}
                </VStack>
              </Flex>
            ))
          ) : (
            <Flex flexDir="column" w="100%">
              <ConsentFormSectionHeader title="Practice" />
              <Text px={4} py={2} bg="white" width="100%" color="gray.600">
                No practice consent forms required
              </Text>
            </Flex>
          )}
        </Flex>
        {missingConsentForms.length ? (
          <MissingFormsView
            missingForms={missingConsentForms}
            patientId={patientId}
            assessmentId={assessmentId}
          />
        ) : null}
        {selectedConsentForm && (
          <DocumentViewModal
            document={selectedConsentForm}
            onFormSubmit={onSignConsentForm}
            value={selectedConsentForm.text ?? []}
            version={selectedConsentForm.editorVersion ?? 'v2'}
            isOpen={!!selectedConsentForm}
            onClose={handleCloseModal}
            withDownload={canDownload}
          />
        )}
      </Flex>
    </ElectronicConsentProvider>
  )
}

const MissingFormsView = ({
  missingForms,
  patientId,
  assessmentId,
}: {
  missingForms: MissingConsentForm[]
  patientId: string | null
  assessmentId: string | null
}) => (
  <Box
    w="100%"
    bg="white"
    _dark={{ bg: 'gray.900' }}
    borderRadius="md"
    borderWidth="1px"
    borderColor="gray.200"
    overflow="hidden">
    <MissingForms missingForms={missingForms} patientId={patientId} assessmentId={assessmentId} />
  </Box>
)

export const ConsentFormsStatusPatientView = ({
  consentFormsStatus,
  patientId,
}: {
  consentFormsStatus: UseConsentFormsStatus
  patientId: string | null
}) => {
  const {
    hbConsentForms,
    practiceConsentForms,
    selectedConsentForm,
    // consentForms,
    handleViewConsentForm,
    handleCloseModal,
    onSignConsentForm,
    canDownload,
  } = consentFormsStatus ?? {}
  const allConsentForms = useMemo(
    () => [...hbConsentForms, ...practiceConsentForms.map(practice => practice.forms).flat()],
    [hbConsentForms, practiceConsentForms],
  )

  const [electronicConsentModalOpen, setElectronicConsentModalOpen] = useState(false)

  return (
    <ElectronicConsentProvider
      patientId={patientId}
      modalOpen={electronicConsentModalOpen}
      setModalOpen={setElectronicConsentModalOpen}>
      <Flex flexDir="column" w="100%">
        <Flex flexFlow="column" w="100%">
          <ElectronicConsentPreview openModal={() => setElectronicConsentModalOpen(true)} />
          <Divider />
          <VStack spacing={0} alignItems="flex-start" w="100%" divider={<Divider />}>
            {allConsentForms.length ? (
              allConsentForms.map(form => (
                // const consentForm = consentForms?.[form.consentFormId]
                //   ? { id: form.consentFormId, ...consentForms[form.consentFormId] }
                //   : null
                <ConsentFormStatusView
                  key={form.consentFormId}
                  patientId={patientId}
                  formStatus={form}
                  // consentForm={consentForm}
                  handleViewConsentForm={handleViewConsentForm}
                />
              ))
            ) : (
              <Text px={4} py={2} bg="white" width="100%" color="gray.600">
                No consent forms required
              </Text>
            )}
          </VStack>
        </Flex>
        {selectedConsentForm && (
          <DocumentViewModal
            document={selectedConsentForm}
            onFormSubmit={onSignConsentForm}
            value={selectedConsentForm.text ?? []}
            version={selectedConsentForm.editorVersion ?? 'v2'}
            isOpen={!!selectedConsentForm}
            onClose={handleCloseModal}
            withDownload={canDownload}
          />
        )}
      </Flex>
    </ElectronicConsentProvider>
  )
}
